import {skipToken} from '@reduxjs/toolkit/query';
import {Button, closeDialog, DataStatus, openDialog, showNotification} from 'platform/components';
import {Heading, HStack, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  useGetBusinessCaseQuery,
  useGetServiceCasesByBusinessCaseQuery,
  usePostServiceCasesByBusinessCaseMutation,
} from '@dms/api/sales';
import {
  useGetSaleVehicleFromSaleVehicleIdQuery,
  useGetVehicleQuery,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import {
  usePostAvailableXentryOrdersMutation,
  usePostIntegrationsForXentryLinkMutation,
  usePostServiceCaseIntegrationsXentryOrderManualCreationMutation,
  usePostServiceCaseMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, workshopRoutes} from '@dms/routes';
import {
  handleApiError,
  LinkXentry,
  NoPermissionTooltip,
  queryParams,
  Section,
  usePermissions,
  workshopSearchParams,
} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import {EmptyState} from './components/EmptyState';
import {ServiceCaseCard} from './components/ServiceCaseCard';

export function BusinessCaseServiceCases() {
  const navigate = useNavigate();
  const {id: businessCaseId} = useRequiredParams();

  const {
    data: businessCase,
    isLoading: isLoadingBC,
    isError: isErrorBC,
  } = useGetBusinessCaseQuery({businessCaseId});

  const saleVehicleId = businessCase?.saleVehicleId ?? null;

  const {
    data: saleVehicle,
    isLoading: isLoadingSaleVehicle,
    isError: isErrorSaleVehicle,
  } = useGetSaleVehicleFromSaleVehicleIdQuery(
    isNotNil(saleVehicleId) ? {vehicleId: saleVehicleId} : skipToken
  );

  const vehicleId = saleVehicle?.vehicleId ?? null;

  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);

  const {
    data: serviceCases = [],
    isLoading: isLoadingServiceCases,
    isError: isErrorServiceCases,
  } = useGetServiceCasesByBusinessCaseQuery({businessCaseId});

  const isLoading =
    isLoadingBC || isLoadingServiceCases || isLoadingSaleVehicle || isLoadingVehicle;
  const isError = isErrorBC || isErrorServiceCases || isErrorSaleVehicle || isErrorVehicle;

  const customerId = businessCase?.customerId ?? null;
  const returnUrl = composePath(businessCaseRoutes.serviceCases, {
    params: {id: businessCaseId},
    isSearchParamsPreserved: false,
  });

  const [canCreate] = usePermissions({permissionKeys: ['serviceCaseCreate']});

  const [postServiceCase, {isLoading: isCreating}] = usePostServiceCaseMutation();
  const [postServiceCasesByBusinessCase] = usePostServiceCasesByBusinessCaseMutation();

  const [postAvailableXentryOrders] = usePostAvailableXentryOrdersMutation({
    fixedCacheKey: 'open-link-xentry',
  });
  const [postIntegrationsForXentryLink] = usePostIntegrationsForXentryLinkMutation();
  const [postServiceCaseIntegrationsXentryOrderManualCreation] =
    usePostServiceCaseIntegrationsXentryOrderManualCreationMutation();

  const handleCreateServiceCase = () => {
    if (!vehicle?.type || !customerId) {
      return showNotification.error(i18n.t('entity.businessCase.errors.cannotCreateServiceCase'));
    }

    postServiceCase({
      body: {
        vehicleId: vehicle.id,
        customerId,
        make: vehicle.make ?? '',
        type: (vehicle.type as VehicleTypeEnum) ?? '',
        modelFamily: vehicle.modelFamily ?? null,
        vin: vehicle.vin ?? null,
        registrationPlate: vehicle?.registrationPlate ?? null,
      },
    })
      .unwrap()
      .then((response) => {
        const newServiceCaseId = response?.id ?? '';
        const draftPath = composePath(workshopRoutes.draftServiceCase, {
          params: {id: newServiceCaseId},
          queryParams: {
            [queryParams.COMPONENT_SECTIONS_TAB]: workshopSearchParams.create.serviceJobs,
            [queryParams.RETURN_URL]: returnUrl,
            [queryParams.REDIRECT_SOURCE]: 'business-case',
          },
        });

        if (response?.additional_action?.includes('MERCEDES_XENTRY_LINK')) {
          postAvailableXentryOrders({serviceCaseId: newServiceCaseId})
            .unwrap()
            .catch(handleApiError);

          openDialog(
            <LinkXentry
              serviceCaseId={newServiceCaseId}
              onClose={() => closeDialog('selectXentryDialog')}
              onLink={(xentryOrderId) =>
                postIntegrationsForXentryLink({
                  serviceCaseId: newServiceCaseId,
                  body: {xentryOrderId},
                })
                  .unwrap()
                  .then(() => closeDialog('selectXentryDialog'))
                  .catch(handleApiError)
              }
              onLinkNew={() =>
                postServiceCaseIntegrationsXentryOrderManualCreation({
                  serviceCaseId: newServiceCaseId,
                })
                  .unwrap()
                  .then(() => closeDialog('selectXentryDialog'))
                  .catch(handleApiError)
              }
              data-testid={testIds.businessCase.detail('linkXentry')}
            />,
            {
              title: i18n.t('entity.integration.labels.selectXentryOrder'),
              id: 'selectXentryDialog',
              onCloseComplete: () => {
                postServiceCasesByBusinessCase({
                  businessCaseId,
                  body: [{serviceCaseId: newServiceCaseId}],
                })
                  .unwrap()
                  .then(() => navigate(draftPath))
                  .catch(handleApiError);
              },
            }
          );
        } else {
          postServiceCasesByBusinessCase({
            businessCaseId,
            body: [{serviceCaseId: newServiceCaseId}],
          })
            .unwrap()
            .then(() => navigate(draftPath))
            .catch(handleApiError);
        }
      })
      .catch(handleApiError);
  };

  return (
    <Section>
      <DataStatus isLoading={isLoading} isError={isError}>
        {serviceCases.length === 0 ? (
          <NoPermissionTooltip shouldShowTooltip={!canCreate}>
            <EmptyState
              onCreateClick={handleCreateServiceCase}
              isLoading={isCreating}
              isDisabled={!canCreate}
            />
          </NoPermissionTooltip>
        ) : (
          <VStack spacing={4}>
            <HStack justify="space-between" align="center">
              <Heading size={3} isSingleLine>
                {i18n.t('entity.businessCase.labels.serviceCases')}
              </Heading>
              <NoPermissionTooltip shouldShowTooltip={!canCreate}>
                <Button
                  variant="primary"
                  title={i18n.t('entity.businessCase.actions.createServiceCase')}
                  onClick={handleCreateServiceCase}
                  isDisabled={!canCreate}
                  isLoading={isCreating}
                />
              </NoPermissionTooltip>
            </HStack>
            {serviceCases.map((sc) => (
              <ServiceCaseCard
                key={sc.serviceCaseId}
                serviceCase={sc}
                isInitiallyOpen={serviceCases.length === 1}
                returnUrl={returnUrl}
              />
            ))}
          </VStack>
        )}
      </DataStatus>
    </Section>
  );
}
